import React, { useState } from 'react';
import { PlusCircleIcon, XIcon } from 'lucide-react';
interface Step2OverviewProps {
  formData: any;
  updateFormData: (data: any) => void;
}
const Step2Overview: React.FC<Step2OverviewProps> = ({
  formData,
  updateFormData
}) => {
  const [learningOutcomes, setLearningOutcomes] = useState<string[]>(formData.learningOutcomes?.length ? formData.learningOutcomes : ['']);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    updateFormData({
      [name]: value
    });
  };
  const handleLearningOutcomeChange = (index: number, value: string) => {
    const updatedOutcomes = [...learningOutcomes];
    updatedOutcomes[index] = value;
    setLearningOutcomes(updatedOutcomes);
    updateFormData({
      learningOutcomes: updatedOutcomes
    });
  };
  const addLearningOutcome = () => {
    const updatedOutcomes = [...learningOutcomes, ''];
    setLearningOutcomes(updatedOutcomes);
    updateFormData({
      learningOutcomes: updatedOutcomes
    });
  };
  const removeLearningOutcome = (index: number) => {
    if (learningOutcomes.length > 1) {
      const updatedOutcomes = learningOutcomes.filter((_, i) => i !== index);
      setLearningOutcomes(updatedOutcomes);
      updateFormData({
        learningOutcomes: updatedOutcomes
      });
    }
  };
  return <div data-id="element-383">
      <h2 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-384">
        Program Overview
      </h2>
      <p className="text-gray-600 mb-6" data-id="element-385">
        Provide essential information about the program to help students
        understand its purpose and benefits.
      </p>
      <div className="space-y-6" data-id="element-386">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-387">
          <div data-id="element-388">
            <label htmlFor="programName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-389">
              Program Name <span className="text-red-500" data-id="element-390">*</span>
            </label>
            <input type="text" id="programName" name="programName" value={formData.programName || ''} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="e.g., Master of Business Administration" required data-id="element-391" />
          </div>
          <div data-id="element-392">
            <label htmlFor="programType" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-393">
              Program Type <span className="text-red-500" data-id="element-394">*</span>
            </label>
            <select id="programType" name="programType" value={formData.programType || ''} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" required data-id="element-395">
              <option value="" data-id="element-396">Select program type</option>
              <option value="Bachelor's" data-id="element-397">Bachelor's Degree</option>
              <option value="Master's" data-id="element-398">Master's Degree</option>
              <option value="Doctorate" data-id="element-399">Doctorate / PhD</option>
              <option value="Diploma" data-id="element-400">Diploma</option>
              <option value="Certificate" data-id="element-401">Certificate</option>
              <option value="Short Course" data-id="element-402">Short Course</option>
            </select>
          </div>
        </div>
        <div data-id="element-403">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-404">
            Program Description <span className="text-red-500" data-id="element-405">*</span>
          </label>
          <textarea id="description" name="description" value={formData.description || ''} onChange={handleInputChange} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="Provide a detailed description of the program..." required data-id="element-406" />
          <p className="text-sm text-gray-500 mt-1" data-id="element-407">
            Describe the program's purpose, structure, and benefits for
            students.
          </p>
        </div>
        <div data-id="element-408">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-409">
            Learning Outcomes <span className="text-red-500" data-id="element-410">*</span>
          </label>
          <p className="text-sm text-gray-500 mb-3" data-id="element-411">
            List the key skills and knowledge students will gain from this
            program.
          </p>
          <div className="space-y-3" data-id="element-412">
            {learningOutcomes.map((outcome, index) => <div key={index} className="flex items-start" data-id="element-413">
                <div className="flex-grow" data-id="element-414">
                  <div className="flex items-center" data-id="element-415">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary-50 text-primary-500 text-sm font-medium mr-2" data-id="element-416">
                      {index + 1}
                    </span>
                    <input type="text" value={outcome} onChange={e => handleLearningOutcomeChange(index, e.target.value)} className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="e.g., Apply strategic thinking to complex business problems" data-id="element-417" />
                  </div>
                </div>
                <button type="button" onClick={() => removeLearningOutcome(index)} className="ml-2 p-2 text-gray-400 hover:text-gray-600" disabled={learningOutcomes.length <= 1} data-id="element-418">
                  <XIcon className="w-5 h-5" data-id="element-419" />
                </button>
              </div>)}
          </div>
          <button type="button" onClick={addLearningOutcome} className="mt-3 flex items-center text-sm text-primary-500 hover:text-primary-600" data-id="element-420">
            <PlusCircleIcon className="w-4 h-4 mr-1" data-id="element-421" />
            Add Another Outcome
          </button>
        </div>
        <div data-id="element-422">
          <label htmlFor="deliveryMode" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-423">
            Mode of Delivery <span className="text-red-500" data-id="element-424">*</span>
          </label>
          <select id="deliveryMode" name="deliveryMode" value={formData.deliveryMode || ''} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" required data-id="element-425">
            <option value="" data-id="element-426">Select delivery mode</option>
            <option value="In-Person" data-id="element-427">In-Person</option>
            <option value="Online" data-id="element-428">Online</option>
            <option value="Hybrid" data-id="element-429">Hybrid (Online & In-Person)</option>
          </select>
        </div>
      </div>
    </div>;
};
export default Step2Overview;